import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { isChannelNotFoundError } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { getPinnedConversationLimitErrorData } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { type Conversation } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useTasksStoreActions as useUnifiedTasksStoreActions } from '@atlassian/conversation-assistant-store/controllers/unified-tasks';

import { useChatStore } from '../chat-store/useChatStore';
import { useChatStoreActions } from '../chat-store/useChatStoreActions';

type UpdateConversationData = Partial<
	Pick<Conversation, 'name' | 'readStatus' | 'isPinned' | 'pinnedAt'>
>;

/**
 * TODO: Deprecated; this is only used in navigation hook as a stop gap solution,
 * we need to use the centralized store actions instead, do not use this for any other cases
 *
 * Conversation rename and delete actions that are usable outside of ChatStoreContainer
 * via createHookWithAssistanceService
 */
export const useConversationActions: (
	props: Omit<AssistanceServiceInjectedParams, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => {
	deleteConversation: ({ conversationId }: { conversationId: string }) => Promise<void>;
	updateConversation: ({
		conversationId,
		data,
	}: {
		conversationId: string;
		data: UpdateConversationData;
	}) => Promise<void>;
} = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams: { assistanceService, experienceTracker },
	}: AssistanceServiceInjectedParams) => {
		const [{ conversations, currentConversationId }] = useChatStore();
		const { removeConversationFromStore, setConversationData, setSelectedConversation } =
			useChatStoreActions();
		const { removeTasksForConversation: unifiedRemoveTasksForConversation } =
			useUnifiedTasksStoreActions();

		const deleteConversationWithAssistanceService = async ({
			conversationId,
		}: {
			conversationId: string;
		}) => {
			experienceTracker.start({
				name: ExperienceName.CLEAR_CHAT,
				id: conversationId,
				attributes: {
					conversationId,
				},
			});

			conversations?.[conversationId]?.storeAbortController?.abort();

			try {
				await assistanceService.deleteChat(conversationId);
			} catch (error: unknown) {
				if (isChannelNotFoundError(error)) {
					removeConversationFromStore({ conversationId });

					if (currentConversationId === conversationId) {
						setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID });
					}
				}

				experienceTracker.fail({
					name: ExperienceName.CLEAR_CHAT,
					error: error instanceof Error ? error : new Error(String(error)),
				});

				throw error;
			}

			removeConversationFromStore({ conversationId });

			unifiedRemoveTasksForConversation(conversationId);

			if (currentConversationId === conversationId) {
				// reset the selected conversation to the empty conversation id
				// this is stop gap solution until we start to use the new conversation store actions,
				// without this the user will be stuck on the deleted conversation as that is the last selected conversation
				setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID });
			}

			experienceTracker.succeed({
				name: ExperienceName.CLEAR_CHAT,
			});
		};

		const updateConversationWithAssistanceService = async ({
			conversationId,
			data,
		}: {
			conversationId: string;
			data: UpdateConversationData;
		}) => {
			experienceTracker.start({
				name: ExperienceName.UPDATE_CONVERSATION_CHANNEL,
				id: conversationId,
				attributes: {
					conversationId,
				},
			});

			let updatedConversation;
			try {
				updatedConversation = await assistanceService.updateConversationChannel(
					conversationId,
					data,
				);
			} catch (error: unknown) {
				const pinLimitErrorData = data.isPinned
					? getPinnedConversationLimitErrorData(error)
					: undefined;
				if (pinLimitErrorData !== undefined) {
					experienceTracker.abort({
						name: ExperienceName.UPDATE_CONVERSATION_CHANNEL,
						reason: 'pinned_conversation_limit_reached',
						attributes: {
							conversationId,
							...pinLimitErrorData,
						},
					});
				} else {
					experienceTracker.fail({
						name: ExperienceName.UPDATE_CONVERSATION_CHANNEL,
						error: error instanceof Error ? error : new Error(String(error)),
					});
				}

				throw error;
			}

			const nextData = { ...data };
			if (updatedConversation) {
				if (updatedConversation.name !== undefined) {
					nextData.name = updatedConversation.name;
				}
				if (updatedConversation.readStatus !== undefined) {
					nextData.readStatus = updatedConversation.readStatus;
				}
				if (typeof updatedConversation.isPinned === 'boolean') {
					nextData.isPinned = updatedConversation.isPinned;
				}
				if (updatedConversation.pinnedAt !== undefined) {
					nextData.pinnedAt = updatedConversation.pinnedAt;
				}
			}

			setConversationData({
				conversationId,
				data: nextData,
			});

			experienceTracker.succeed({
				name: ExperienceName.UPDATE_CONVERSATION_CHANNEL,
			});
		};

		return {
			deleteConversation: deleteConversationWithAssistanceService,
			updateConversation: updateConversationWithAssistanceService,
		};
	},
);
